import PropTypes from 'prop-types';
import Box from 'components/UI/Box/Box';
import { ReactComponent as MedalRibbonsStarLogo } from 'assets/icons/medal-ribbons-star.svg';
import Button from 'components/UI/Button';
import LuchitoSrc from 'assets/images/luchito-promotion.png';
import { formatDate } from 'utils/formatDate';
import { ROUTE_PATH } from 'routes';
import { ReactComponent as EyeIcon } from 'assets/icons/eye.svg';
import { formatCurrency, formatNumber } from 'utils/formatCurrency';

const normalizeCouponType = (couponType) => String(couponType || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase();

/** Presentación según el texto de des_tipo_cupon, sin traducir el número. */
export const getCouponCardVariant = (couponType) => {
  const value = normalizeCouponType(couponType);
  if (value === 'venta') return 'venta';
  if (value === 'empeno') return 'empeno';
  return 'default';
};

const FieldRow = ({ label, value, className = 'mb-2' }) => (
  <div className={`flex flex-wrap items-baseline gap-x-2 gap-y-1 ${className}`}>
    <p className='mb-0'>{label}</p>
    <p className='mb-0 font-semibold'>{value}</p>
  </div>
);

FieldRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  className: PropTypes.string,
};

const Promotion = ({
  literals, points, promotion, onCreateCoupon,
}) => {
  const disabled = (points?.available || 0) < promotion.points && !promotion.couponId;
  const variant = getCouponCardVariant(promotion.couponType);
  const categories = promotion.sections
    .map((section) => literals.sections[section] || section)
    .join(', ');
  const pointsLabel = promotion.couponId ? literals.pointsUsed : literals.pointsToUse;
  const discountLabel = {
    venta: literals.discountOnSale,
    empeno: literals.discountOnInterest,
  }[variant] || literals.discount;
  const applyLabel = {
    venta: literals.applyInPurchases,
    empeno: literals.applyInInterest,
  }[variant] || literals.applyIn;
  const maxAmountLabel = variant === 'empeno'
    ? literals.maxInterestDiscount
    : literals.maxAmount;
  const showMaxAmount = variant !== 'venta';

  return (
    <Box className='card-loyalty-promotion mb-4 relative' padding='sm'>
      <div className='flex items-center gap-2 mb-4'>
        <MedalRibbonsStarLogo width={48} />
        <p className='mb-0 font-bold text-lg'>{promotion.description}</p>
      </div>
      <p className='text-sm text-neutral-500 mt-2 mb-2'>{`#${promotion.id}`}</p>
      {variant === 'default' ? (
        <>
          <FieldRow
            label={pointsLabel}
            value={formatNumber(promotion.points, 0)}
          />
          <FieldRow
            label={discountLabel}
            value={`${promotion.discount}%`}
          />
          {promotion.couponType ? (
            <FieldRow label={literals.couponType} value={promotion.couponType} />
          ) : null}
          {categories ? (
            <FieldRow label={applyLabel} value={categories} />
          ) : null}
          <FieldRow
            label={literals.life}
            value={formatDate(promotion.endDate, 'DD/MM/YYYY')}
          />
          <FieldRow
            label={maxAmountLabel}
            value={formatCurrency(promotion.maxAmount)}
            className='mb-6'
          />
        </>
      ) : (
        <>
          {promotion.couponType ? (
            <FieldRow label={literals.couponType} value={promotion.couponType} />
          ) : null}
          <FieldRow
            label={pointsLabel}
            value={formatNumber(promotion.points, 0)}
          />
          <FieldRow
            label={discountLabel}
            value={`${promotion.discount}%`}
          />
          {showMaxAmount ? (
            <FieldRow
              label={maxAmountLabel}
              value={formatCurrency(promotion.maxAmount)}
            />
          ) : null}
          <FieldRow
            label={literals.life}
            value={formatDate(promotion.endDate, 'DD/MM/YYYY')}
            className={categories ? 'mb-2' : 'mb-6'}
          />
          {categories ? (
            <FieldRow label={applyLabel} value={categories} className='mb-6' />
          ) : null}
        </>
      )}
      {promotion.autosMessage ? (
        <p className='alert alert-warning text-sm mb-4' role='alert'>
          {promotion.autosMessage}
        </p>
      ) : null}
      {promotion.couponId ? (
        <Button
          size='sm'
          className='w-1/2 flex items-center gap-2 justify-center'
          to={ROUTE_PATH.setLoyaltyPointsCoupon(promotion.couponId)}
        >
          <EyeIcon width={16} />
          {literals.goToCoupon}
        </Button>
      ) : (
        <Button
          text={literals.createCoupon}
          size='sm'
          className='w-1/2'
          onClick={() => onCreateCoupon(promotion)}
        />
      )}
      <img
        className='absolute bottom-0 img-promotion'
        src={LuchitoSrc}
        alt='Luchito'
        width={120}
      />
      {disabled && (
        <div className='promotion-disabled'>
          <p className='mb-0'>{literals.notEnoughPoints}</p>
        </div>
      )}
    </Box>
  );
};

Promotion.propTypes = {
  literals: PropTypes.object.isRequired,
  points: PropTypes.object.isRequired,
  promotion: PropTypes.object.isRequired,
  onCreateCoupon: PropTypes.func.isRequired,
};

export default Promotion;
